import React, { useState, useEffect } from 'react'
import { Container, Tabs, Tab } from 'react-bootstrap'
import { useLocation, useNavigate } from 'react-router-dom'
import RecentlyReceivedWidget from '../widgets/RecentlyReceivedWidget'
import ExpiringItemsWidget from '../widgets/ExpiringItemsWidget'
import OutOfStockItemsWidget from '../widgets/OutOfStockItemsWidget'
import BestSellersWidget from '../widgets/BestSellersWidget'
import SlowSellersWidget from '../widgets/SlowSellersWidget'
import ReorderAlertsWidget from '../widgets/ReorderAlertsWidget'

function DashboardScreen() {
    const location = useLocation()
    const navigate = useNavigate()
    const [activeWidget, setActiveWidget] = useState('recently-received')
    const [widgetStateToRestore, setWidgetStateToRestore] = useState(null)

    const widgets = [
        {
            id: 'recently-received',
            name: 'Recently Received',
            component: RecentlyReceivedWidget,
            icon: '📦⬇️',
            color: 'var(--bs-success)'
        },
        {
            id: 'expiring-items',
            name: 'Expiring Items',
            component: ExpiringItemsWidget,
            icon: '📅❗',
            color: 'var(--bs-warning)'
        },
        {
            id: 'out-of-stock',
            name: 'Out of Stock Items',
            component: OutOfStockItemsWidget,
            icon: '📦❌',
            color: 'var(--bs-danger)'
        },
        {
            id: 'best-sellers',
            name: 'Best Sellers',
            component: BestSellersWidget,
            icon: '🏆',
            color: 'var(--bs-success)'
        },
        {
            id: 'slow-sellers',
            name: 'Slow Sellers',
            component: SlowSellersWidget,
            icon: '🐌',
            color: 'var(--bs-warning)'
        },
        {
            id: 'reorder-alerts',
            name: 'Reorder Alerts',
            component: ReorderAlertsWidget,
            icon: '🔔',
            color: 'var(--bs-danger)'
        }
    ]

    const activeWidgetConfig = widgets.find(widget => widget.id === activeWidget)
    const ActiveComponent = activeWidgetConfig?.component

    // Handle widget state restoration when returning from PO creation
    useEffect(() => {
        if (location.state?.restoreWidget?.type === 'OutOfStockItems') {
            console.log('Dashboard: Restoring OutOfStockItems widget state');
            
            // Set active widget to out-of-stock
            setActiveWidget('out-of-stock');
            
            // Store the widget state to be passed to the widget
            setWidgetStateToRestore(location.state.restoreWidget.state);
            
            // Clear the restoration state to prevent re-triggering
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    // Render widget icon with overlay if needed
    const renderWidgetIcon = (widgetId, icon) => {
        if (widgetId === 'recently-received') {
            return (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <span>📦</span>
                    <span style={{
                        position: 'absolute',
                        top: '40%',
                        left: '80%',
                        fontSize: '0.8rem',
                        transform: 'translate(-50%, -10%)',
                        zIndex: 1
                    }}>⬇️</span>
                </div>
            )
        } else if (widgetId === 'out-of-stock') {
            return (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <span>📦</span>
                    <span style={{
                        position: 'absolute',
                        top: '40%',
                        left: '80%',
                        fontSize: '0.8rem',
                        transform: 'translate(-50%, -10%)',
                        zIndex: 1
                    }}>🚫</span>
                </div>
            )
        } else if (widgetId === 'expiring-items') {
            return (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <span>📅</span>
                    <span style={{
                        position: 'absolute',
                        top: '40%',
                        left: '80%',
                        fontSize: '0.8rem',
                        zIndex: 1
                    }}>❗</span>
                </div>
            )
        } else {
            return <span>{icon}</span>
        }
    }

    // Render the active widget component
    const renderActiveWidget = () => {
        const widget = activeWidgetConfig
        if (!widget) return null

        if (!widget.component) {
            return (
                <div className="d-flex flex-column align-items-center justify-content-center p-5">
                    <div className="text-muted mb-3">
                        <i className="bi bi-tools" style={{ fontSize: '3rem' }}></i>
                    </div>
                    <h6 className="text-muted">{widget.name}</h6>
                    <p className="text-muted small">This widget is coming soon!</p>
                </div>
            )
        }

        // Special handling for OutOfStockItems widget with state restoration
        if (widget.id === 'out-of-stock') {
            return (
                <widget.component
                    restoreState={widgetStateToRestore}
                    onStateRestored={() => setWidgetStateToRestore(null)}
                />
            )
        }

        // Render other widgets normally
        return <widget.component />
    }

    return (
        <Container fluid className="px-4 py-4">
            <style>
                {`
                    .custom-tabs .nav-link {
                        color: var(--bs-body-color) !important;
                        border: 2px solid transparent !important;
                        transition: all 0.3s ease !important;
                        border-radius: 10px 10px 0 0 !important;
                        margin-right: 4px !important;
                        opacity: 0.7;
                    }

                    .custom-tabs .nav-link:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                        opacity: 1;
                    }

                    .custom-tabs .nav-link.active {
                        border-bottom: 2px solid ${activeWidgetConfig?.color || 'var(--bs-primary)'} !important;
                        background: linear-gradient(135deg, ${activeWidgetConfig?.color || 'var(--bs-primary)'}10, ${activeWidgetConfig?.color || 'var(--bs-primary)'}05) !important;
                        color: ${activeWidgetConfig?.color || 'var(--bs-primary)'} !important;
                        font-weight: 600 !important;
                        box-shadow: 0 0 20px ${activeWidgetConfig?.color || 'var(--bs-primary)'}40,
                                    0 4px 15px rgba(0,0,0,0.1) !important;
                        transform: translateY(-3px);
                        opacity: 1 !important;
                    }

                    .custom-tabs .tab-content {
                        border: none !important;
                        padding: 0 !important;
                    }
                `}
            </style>
            <Tabs
                activeKey={activeWidget}
                onSelect={(key) => setActiveWidget(key)}
                className="mb-3 custom-tabs"
            >
                {widgets.map((widget) => (
                    <Tab
                        key={widget.id}
                        eventKey={widget.id}
                        title={
                            <div className="d-flex align-items-center gap-2">
                                <span style={{ fontSize: '1.2rem' }}>
                                    {renderWidgetIcon(widget.id, widget.icon)}
                                </span>
                                <span>{widget.name}</span>
                            </div>
                        }
                    >
                        {/* Empty - content rendered below */}
                    </Tab>
                ))}
            </Tabs>

            {/* Lazy-loaded widget content */}
            <div className="widget-content">
                {renderActiveWidget()}
            </div>
        </Container>
    )
}

export default DashboardScreen